/**
 * ProductCategoryPicker — single-select FK combobox for a product category, with
 * inline "+ Create" (the canonical EntityPickerBase + QuickCreateDialog pattern).
 *
 * Categories are hierarchical: the list is built from the category tree and each
 * row is indented by its depth and labelled with its "Parent / Child" path. Pass
 * `excludeIds` to hide a subtree (used by the parent-category picker to prevent
 * cycles by excluding self + descendants).
 *
 * See docs/design-system/ui-detail-and-form-pages.md § FK Picker with Inline Create.
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Boxes } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { EntityPickerBase } from '../../../../shared/components/common';
import { useEmbeddingPrefix } from '../../../../shared/hooks';
import { useProductCategoryTree } from '../hooks/useProductCategories';
import { ProductCategoryQuickCreateDialog } from './ProductCategoryQuickCreateDialog';
import type { ProductCategory, ProductCategoryTreeNode } from '../types';

/** A flattened category row carrying its depth and full ancestor path. */
interface FlatCategory extends ProductCategory {
  depth: number;
  path: string;
}

/** Depth-first flatten of the category tree into ordered rows with path labels. */
function flattenTree(
  nodes: ProductCategoryTreeNode[],
  excludeIds: Set<string>,
  depth = 0,
  parentPath = '',
): FlatCategory[] {
  const rows: FlatCategory[] = [];
  for (const node of nodes) {
    if (excludeIds.has(node.id)) continue; // also drops the whole subtree
    const path = parentPath ? `${parentPath} / ${node.name}` : node.name;
    const { children, ...category } = node;
    rows.push({ ...category, depth, path });
    if (children.length > 0) {
      rows.push(...flattenTree(children, excludeIds, depth + 1, path));
    }
  }
  return rows;
}

interface ProductCategoryPickerProps {
  /** Currently selected category ID ('' when none) */
  value: string;
  /** Callback when a category is selected (id) or cleared ('') */
  onChange: (categoryId: string) => void;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder in the trigger when nothing is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show the "None" option to clear selection */
  allowClear?: boolean;
  /** Required indicator on the label */
  required?: boolean;
  /** Hide the "Add new" action (filter / parent-select contexts) */
  hideAddNew?: boolean;
  /** Render the selected category as a link to its detail page */
  linkToEntity?: boolean;
  /** Category IDs (and their subtrees) to hide — e.g. self + descendants. */
  excludeIds?: string[];
}

export function ProductCategoryPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  allowClear = true,
  required = false,
  hideAddNew = false,
  linkToEntity = false,
  excludeIds,
}: ProductCategoryPickerProps) {
  const { t } = useTranslation(['products', 'common']);
  const { prefix } = useEmbeddingPrefix();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  const { data: tree } = useProductCategoryTree();

  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  const allCategories = useMemo(
    () => flattenTree(tree ?? [], excludeSet),
    [tree, excludeSet]
  );

  const categories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allCategories;
    return allCategories.filter((c) => c.path.toLowerCase().includes(query));
  }, [allCategories, search]);

  const handleSelect = useCallback((categoryId: string) => {
    onChange(categoryId);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleAddNew = useCallback(() => {
    setCreateDialogName(search.trim());
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleCategoryCreated = useCallback((category: ProductCategory) => {
    onChange(category.id);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange]);

  const addNewLabel = search.trim()
    ? t('categories.createWithName', { name: search.trim() })
    : t('categories.addNew');

  return (
    <>
      <EntityPickerBase<FlatCategory>
        value={value}
        items={categories}
        getItemLabel={(category) => category.path}
        renderItem={(category, isSelected) => (
          <>
            <Check
              className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <Boxes className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span
              className="truncate"
              style={{ paddingInlineStart: `${category.depth * 16}px` }}
            >
              {category.name}
            </span>
            {category.product_count !== undefined && (
              <span className="ms-auto ps-2 text-xs text-muted-foreground shrink-0">
                {category.product_count}
              </span>
            )}
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('categories.searchOrCreate')}
        searchPlaceholder={t('categories.search')}
        emptyMessage={t('categories.noCategories')}
        disabled={disabled}
        className={className}
        required={required}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        hideAddNew={hideAddNew}
        addNewLabel={addNewLabel}
        onAddNew={handleAddNew}
        selectedEntityUrl={linkToEntity && value
          ? `${prefix}/categories/${value}`
          : undefined}
        entityIcon={linkToEntity ? Boxes : undefined}
      />

      <ProductCategoryQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleCategoryCreated}
        skipNavigation
        defaultName={createDialogName}
      />
    </>
  );
}
